// constants/aiConstants.js

// Default AI model to use throughout the application
export const DEFAULT_AI_MODEL = 'gemini-2.5-flash';

// AI Provider types
export const AI_PROVIDERS = {
  OPENAI: 'openai',
  ANTHROPIC: 'anthropic',
  GOOGLE: 'google'
};

// API keys loaded from environment variables
// ⚠️ IMPORTANT: Only Google API key is safe for frontend
// OpenAI and Anthropic keys should ONLY be on backend
const normalizeApiKey = (value) => {
  if (!value) return null;

  const trimmed = String(value).trim();
  const placeholderValues = new Set([
    'your_google_api_key_here',
    'your_openai_key_here',
    'your_anthropic_key_here'
  ]);

  return placeholderValues.has(trimmed.toLowerCase()) ? null : trimmed;
};

export const API_KEYS = {
  [AI_PROVIDERS.OPENAI]: null, // NOT available in frontend - use backend only
  [AI_PROVIDERS.ANTHROPIC]: null, // NOT available in frontend - use backend only
  [AI_PROVIDERS.GOOGLE]: normalizeApiKey(process.env.REACT_APP_GOOGLE_API_KEY)
};

// Available AI models with descriptions and providers
export const AI_MODELS = [
  // OpenAI models
  { 
    id: 'gpt-4o-mini', 
    name: 'GPT-4o Mini (OpenAI)', 
    description: '$0.60 - Fast & efficient for most tasks (Default)',
    provider: AI_PROVIDERS.OPENAI,
    stability: 'high'
  },

  { 
    id: 'gpt-4o', 
    name: 'gpt-4o', 
    description: '$10.00',
    provider: AI_PROVIDERS.OPENAI,
    stability: 'high'
  },
  
   // Claude models
   { 
    id: 'claude-3-7-sonnet-latest', 
    name: 'Claude 3.7 Sonnet', 
    description: '$15 -  Latest Sonnet model - powerful reasoning', 
    provider: AI_PROVIDERS.ANTHROPIC,
    stability: 'high'
  },
  { 
    id: 'claude-3-5-haiku-latest', 
    name: 'Claude 3.5 Haiku', 
    description: '$4.00 - Latest Haiku model - fast responses', 
    provider: AI_PROVIDERS.ANTHROPIC,
    stability: 'high'
  },
  { 
    id: 'claude-3-haiku-20240307', 
    name: 'Claude 3 Haiku', 
    description: '$1.25 - Balanced speed and quality', 
    provider: AI_PROVIDERS.ANTHROPIC,
    stability: 'high'
  },

  // Google Gemini models
  {
    id: 'gemini-3.1-pro-preview',
    name: 'Gemini 3.1 Pro Preview (Google)',
    description: 'Newest top-end Gemini model, preview quality tier',
    provider: AI_PROVIDERS.GOOGLE,
    stability: 'preview',
    experimental: true,
    recommended: true
  },
  {
    id: 'gemini-2.5-pro',
    name: 'Gemini 2.5 Pro (Google)',
    description: 'Latest and most powerful model',
    provider: AI_PROVIDERS.GOOGLE,
    stability: 'high',
    recommended: true
  },
  {
    id: 'gemini-2.5-flash',
    name: 'Gemini 2.5 Flash (Google)',
    description: 'Fast, balanced responses',
    provider: AI_PROVIDERS.GOOGLE,
    stability: 'high'
  },
  {
    id: 'gemini-2.5-flash-lite',
    name: 'Gemini 2.5 Flash-Lite (Google)',
    description: 'Fastest low-cost model',
    provider: AI_PROVIDERS.GOOGLE,
    stability: 'high'
  }
];

// Get model info by ID
export const getModelById = (modelId) => {
  return AI_MODELS.find(model => model.id === modelId) || null;
};

// Get provider for a specific model
export const getProviderForModel = (modelId) => {
  const model = getModelById(modelId);
  return model ? model.provider : AI_PROVIDERS.OPENAI;
};

// Get recommended models for specific tasks
export const getRecommendedModels = (task = 'general') => {
  switch (task) {
    case 'translation':
      return AI_MODELS.filter(model => 
        model.stability === 'high' && 
        (model.provider === AI_PROVIDERS.GOOGLE || model.provider === AI_PROVIDERS.OPENAI)
      ).sort((a, b) => {
        // Prioritize recommended models
        if (a.recommended && !b.recommended) return -1;
        if (!a.recommended && b.recommended) return 1;
        return 0;
      });
    
    case 'fast':
      return AI_MODELS.filter(model => 
        model.description.toLowerCase().includes('fast') ||
        model.description.toLowerCase().includes('mini')
      );
    
    case 'quality':
      return AI_MODELS.filter(model => 
        model.stability === 'high' && 
        !model.experimental
      );
    
    default:
      return AI_MODELS.filter(model => !model.experimental);
  }
};

// Model capabilities and limits
export const MODEL_CAPABILITIES = {
  'gpt-4o-mini': {
    maxInputTokens: 128000,
    maxOutputTokens: 16384,
    supportsImages: true,
    supportsFiles: false,
    bestFor: ['general tasks', 'translation', 'coding']
  },
  'gpt-4o': {
    maxInputTokens: 128000,
    maxOutputTokens: 16384,
    supportsImages: true,
    supportsFiles: false,
    bestFor: ['complex reasoning', 'high-quality output']
  },
  'claude-3-7-sonnet-latest': {
    maxInputTokens: 200000,
    maxOutputTokens: 8192,
    supportsImages: true,
    supportsFiles: true,
    bestFor: ['complex reasoning', 'analysis']
  },
  'gemini-3.1-pro-preview': {
    maxInputTokens: 200000,
    maxOutputTokens: 65536,
    supportsImages: true,
    supportsFiles: true,
    bestFor: ['translation', 'analysis', 'complex reasoning']
  },
  'gemini-2.5-pro': {
    maxInputTokens: 1000000,
    maxOutputTokens: 65536,
    supportsImages: true,
    supportsFiles: true,
    bestFor: ['translation', 'analysis', 'complex reasoning']
  },
  'gemini-2.5-flash': {
    maxInputTokens: 1000000,
    maxOutputTokens: 65536,
    supportsImages: true,
    supportsFiles: true,
    bestFor: ['quick responses', 'simple translations']
  },
  'gemini-2.5-flash-lite': {
    maxInputTokens: 1000000,
    maxOutputTokens: 65536,
    supportsImages: true,
    supportsFiles: true,
    bestFor: ['high throughput', 'lowest cost']
  }
};

// Get model capabilities
export const getModelCapabilities = (modelId) => {
  return MODEL_CAPABILITIES[modelId] || {
    maxInputTokens: 4096,
    maxOutputTokens: 2048,
    supportsImages: false,
    supportsFiles: false,
    bestFor: ['general tasks']
  };
};
